import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import { useReCaptcha } from '@/providers/recaptcha';
import { useCountdownFromNow, useIsMobile } from '@scramble/hooks';
import { useGetEmailOtpStatus } from '@/query/queries/auth/useGetEmailOtpStatus';
import { useSubmitEmailOtpCode } from '@/query/queries/auth/useSubmitEmailOtpCode';
import { useResendEmailOtpCode } from '@/query/queries/auth/useResendEmailOtpCode';
import { Button, InlineAlert, Snackbar, VerificationCodeInput } from '@scramble/ui';
import IconArrowLeft from '@scramble/design-system/icons/arrow-left.svg';
import { clearTempUserData, getTempUserData } from '@/pages/AuthPage/utils';
import { OTP_LENGTH } from '@/helpers/constants';
import { QUERY_KEYS } from '@/query/queryKeys';
import s from './index.module.scss';

const SMS_LIMIT = 5;

const VALIDATION_SCHEMA = Yup.object({
  code: Yup.string().required(),
});

export const StepEmailOtp = ({ onBack, onSuccess }) => {
  const user = getTempUserData();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const mounted = useRef(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [expireTime, setExpireTime] = useState(null);

  const onTokenExpired = () => {
    clearTempUserData();
    window.location.reload();
  };

  const { data: OtpStatus } = useGetEmailOtpStatus(user?.email, {
    onTokenExpired,
    enabled: Boolean(user?.email) && !success,
  });
  const { mutateAsync: resendCode } = useResendEmailOtpCode(user?.email, { onTokenExpired });
  const onCaptchaExpired = () => {
    Snackbar({ type: 'warning', text: t('auth:otp.session_expired') });
  };

  const { mutateAsync: submitCode } = useSubmitEmailOtpCode({ onTokenExpired, onCaptchaExpired });
  const { token: captchaToken, loaded, getCaptchaToken } = useReCaptcha();
  const isMobile = useIsMobile();

  const timeLeft = useCountdownFromNow(expireTime, () => {
    setExpireTime(null);
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (!success) return;

      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.auth.email_otp_status],
        exact: false,
      });
    };
  }, [success, queryClient]);

  useEffect(() => {
    const lastCreated = OtpStatus?.data.last_created_at;
    if (lastCreated) {
      const currentTime = moment();
      const expireTime = moment(OtpStatus.data.last_created_at).add(
        OtpStatus.data.count >= SMS_LIMIT ? 10 : 1,
        'minutes',
      );

      mounted.current && currentTime.isBefore(expireTime)
        ? setExpireTime(expireTime.toISOString())
        : setExpireTime(null);
    } else {
      mounted.current && setExpireTime(null);
    }
  }, [OtpStatus]);

  const onSubmit = async ({ code }, { setErrors }) => {
    if (!loaded) return;
    const recaptchaToken = captchaToken || (await getCaptchaToken());
    if (!recaptchaToken) return;
    const res = await submitCode({
      code,
      recaptcha_token: recaptchaToken,
    });

    if (res?.success) {
      setSuccess(true);
      setTimeout(() => {
        onSuccess(res.data);
      }, 500); // use delay to show visual green effects
    }

    if (res?.captchaExpired) setFieldValue('code', '');

    if (res?.error) setErrors({ code: res.error });
  };

  const { values, errors, touched, setFieldValue, handleSubmit } = useFormik({
    onSubmit,
    validationSchema: VALIDATION_SCHEMA,
    initialValues: { code: '' }
  });

  useEffect(() => {
    if (loaded && values.code.length === OTP_LENGTH) {
      handleSubmit();
    }
  }, [values.code, handleSubmit, loaded]);

  const onResendCode = async () => {
    setSending(true);
    await resendCode();
    await setFieldValue('code', '');
    setSending(false);
  };

  if (!OtpStatus) return null;

  const email = OtpStatus.data?.email;
  const hasAttempts = OtpStatus.data?.has_attempts;
  const limitReached = OtpStatus.data.count >= SMS_LIMIT;

  const resendButton = (
    <button
      type="button"
      onClick={onResendCode}
      className={s.btn_resend}
      disabled={sending}
    >
      <span className="f-body-sm f-500">
        {t('auth:otp.resend_code')}
      </span>
    </button>
  )

  return (
    <form onSubmit={handleSubmit} className={s.wrapper}>
      <Button
        type="button"
        theme="neutral"
        appearance="tonal"
        size="md"
        onClick={onBack}
        className={s.btn_back}
        iconUrl={isMobile && IconArrowLeft}
      >
        <img src={IconArrowLeft} alt="back" width={20} height={20} />
        {!isMobile && (
          <span className="f-body-sm f-500">
            {t('auth:step_email_otp.back')}
          </span>
        )}
      </Button>

      <div className={s.content}>
        <div className={s.header}>
          <h1 className="f-headline-sm f-500">
            {t('auth:step_email_otp.check_email')}
          </h1>
          <div className="f-body-md">
            <Trans
              ns="auth"
              i18nKey="step_email_otp.message"
              values={{ email }}
              components={{ Span: <span className="f-600" /> }}
            />
          </div>
        </div>
        <div className={s.input}>
          <VerificationCodeInput
            label={t('auth:otp.verification_code')}
            value={values.code}
            onChange={value => setFieldValue('code', value)}
            error={errors.code && touched.code}
            success={success}
            disabled={limitReached && !timeLeft}
          />
          {errors.code && touched.code && (
            <InlineAlert
              type="danger"
              title={t('auth:otp.invalid_ver_code')}
              message={t('auth:otp.try_again')}
              className={s.input__error}
            />
          )}
        </div>

        {OtpStatus && (
          <div className={s.message}>
            {hasAttempts &&
              (timeLeft ? (
                <div className={s.resend_code_in}>
                  <Trans
                    ns="auth"
                    i18nKey="auth:otp.resend_code_in"
                    components={{
                      Span: <span />,
                      Nowrap: <span className="nowrap" />,
                    }}
                    values={{ time: timeLeft }}
                  />
                </div>
              ) : (
                resendButton
              ))}

            {!hasAttempts &&
              (timeLeft ? (
                <div className="f-body-sm">
                  <Trans
                    ns="auth"
                    i18nKey="auth:step_email_otp.too_many_attempts"
                    components={{
                      Nowrap: <span className="nowrap" />,
                    }}
                    values={{ time: timeLeft }}
                  />
                </div>
              ) : (
                resendButton
              ))}
          </div>
        )}
      </div>
    </form>
  );
};
